/**
 * Как папки приложения («Мои / Без ответа / Все переписки») превращаются в запросы к Chatwoot.
 *
 * Сверено с app/finders/conversation_finder.rb нашей копии:
 *  • «Мои»        → assignee_type=me;
 *  • «Все»        → без assignee_type;
 *  • «Без ответа» → conversation_type=unattended. Фильтр Chatwoot (Conversation.unattended):
 *                   first_reply_created_at IS NULL OR waiting_since IS NOT NULL, то есть
 *                   «клиент ждёт ответа» (waiting_since ставится на письмо клиента и снимается на
 *                   первый не-заметочный ответ) плюс переписки, куда ещё никто не отвечал;
 *  • status[]=open&status[]=pending. 🪤 Штатный вид — только open: переписки, которые держит бот
 *    (pending), в нём пропадают, и раздел выглядит пустым. Массив в status разрешён
 *    (`where(status: params[:status])`), 'all' брать нельзя — это ещё и решённые.
 *  • inbox_id — канал; inbox_stage_id — стадия канала (filter_by_stage → custom_attributes.crm_stage_id).
 */
import type { InboxConversation } from '@/api/inboxTypes';

export type Folder = 'mine' | 'unattended' | 'all';

export const FOLDERS: Array<{ key: Folder; label: string }> = [
  { key: 'mine', label: 'Мои' },
  { key: 'unattended', label: 'Без ответа' },
  { key: 'all', label: 'Все переписки' },
];

export type ConvStatus = 'open' | 'pending';

export interface ListQuery {
  /** unassigned нужен только значку на вкладке. */
  assignee: 'me' | 'unassigned' | 'all';
  unattended: boolean;
  statuses: ConvStatus[];
  inboxId: number | null;
  /** id — для запроса, externalId — чтобы решать, подходит ли переписка из события. */
  stage: { id: number; externalId: string } | null;
  sort: 'activity' | 'unread';
}

export interface ListFilters {
  inboxId: number | null;
  stage: { id: number; externalId: string } | null;
}

/** Папка + выбранный канал/стадия → описание запроса. */
export function folderToQuery(folder: Folder, filters: ListFilters = { inboxId: null, stage: null }): ListQuery {
  return {
    assignee: folder === 'mine' ? 'me' : 'all',
    unattended: folder === 'unattended',
    statuses: ['open', 'pending'],
    inboxId: filters.inboxId,
    stage: filters.stage,
    sort: 'activity',
  };
}

/** Запросы для значка вкладки: непрочитанные у меня и свободные, только open, непрочитанные вперёд. */
export function badgeQueries(): ListQuery[] {
  const base: Omit<ListQuery, 'assignee'> = {
    unattended: false, statuses: ['open'], inboxId: null, stage: null, sort: 'unread',
  };
  return [{ ...base, assignee: 'me' }, { ...base, assignee: 'unassigned' }];
}

/** Параметры GET /api/v1/accounts/:id/conversations(/meta). */
export function toSearchParams(q: ListQuery, page?: number): URLSearchParams {
  const p = new URLSearchParams();
  q.statuses.forEach((s) => p.append('status[]', s));
  if (q.assignee !== 'all') p.set('assignee_type', q.assignee);
  if (q.unattended) p.set('conversation_type', 'unattended');
  if (q.inboxId !== null) p.set('inbox_id', String(q.inboxId));
  if (q.stage) p.set('inbox_stage_id', String(q.stage.id));
  p.set('sort_by', q.sort === 'unread' ? 'unread' : 'last_activity_at_desc');
  if (page !== undefined) p.set('page', String(page));
  return p;
}

/** Устойчивая строка для ключа кэша. */
export function queryKeyPart(q: ListQuery): string {
  return JSON.stringify([q.assignee, q.unattended, q.statuses, q.inboxId, q.stage?.id ?? null, q.sort]);
}

/** Подходит ли переписка под запрос — нужно, чтобы решить, вставлять ли её в кэш по событию. */
export function matchesQuery(c: InboxConversation, q: ListQuery, myUserId: number | null): boolean {
  if (!q.statuses.includes(c.status as ConvStatus)) return false;
  if (q.inboxId !== null && c.inboxId !== q.inboxId) return false;
  if (q.assignee === 'me' && !(c.assigneeKind === 'User' && c.assignee?.id === myUserId)) return false;
  // В Chatwoot у бота свой столбец: «свободная» = нет ЧЕЛОВЕКА-ответственного (assignee_id пуст).
  if (q.assignee === 'unassigned' && c.assigneeKind === 'User') return false;
  if (q.unattended && !(c.firstReplyAt === 0 || c.waitingSince > 0)) return false;
  if (q.stage && c.crmStageId !== q.stage.externalId) return false;
  return true;
}

/** Сколько всего переписок у запроса — по meta Chatwoot (для решения «есть ли следующая страница»). */
export function totalFromMeta(
  meta: { mine_count: number; all_count: number; unassigned_count: number } | undefined,
  q: ListQuery,
): number | null {
  if (!meta) return null;
  if (q.assignee === 'me') return meta.mine_count;
  if (q.assignee === 'unassigned') return meta.unassigned_count;
  return meta.all_count;
}

/** Размер страницы Chatwoot по умолчанию (CONVERSATION_RESULTS_PER_PAGE). */
export const PAGE_SIZE = 25;
